import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, ConfigBaseService, IAccount, IConfiguration, MDS_CONFIG_SERVICE } from '@mds/angular-core';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, Subscription, fromEvent } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { createAuthConfig } from '../config/auth-config.factory';
import { AuthConfigModel, MDS_AUTH_CONFIG_MAP, MDS_AUTH_DEBUG } from '../models/auth-config.model';
import { ServerSentEvents } from '../models/server-sent-events.model';
import { DynamicScopesService } from './dynamic-scopes.service';

interface OAuthClaims {
  sub: string;
  name: string;
  email: string | undefined;
  idp: string | undefined;
  auth_time: string | undefined;
}

@Injectable()
export class AuthenticationService extends AuthService implements OnDestroy {
  private readonly authState = 'auth-state';
  private readonly authConfigModel$ = new BehaviorSubject<AuthConfigModel | null>(null);
  private readonly oauthEventsSubscription: Subscription;
  private sessionEvents: EventSource | undefined;
  private logoutInProgress = false;

  constructor(
    private readonly oauthService: OAuthService,
    private readonly router: Router,
    private readonly dynamicScopesService: DynamicScopesService,
    @Inject(MDS_CONFIG_SERVICE) configService: ConfigBaseService,
    @Inject(MDS_AUTH_DEBUG) showDebugInformation: boolean,
    @Inject(MDS_AUTH_CONFIG_MAP) authConfigMap: (config: IConfiguration) => AuthConfigModel
  ) {
    super();
    this.isAuthenticatedSubject$.next(false);

    this.oauthEventsSubscription = this.oauthService.events.subscribe((e) => {
      switch (e.type) {
        case 'token_received':
          this.logoutInProgress = false;
          this.setUserIdFromAccount();
          this.startSessionEvents();

          if (this.state) {
            router.navigateByUrl(this.state);
            this.clearState();
          }
          break;

        case 'code_error':
        case 'token_error':
        case 'token_refresh_error':
          this.handleAuthenticationError(e as OAuthErrorEvent);
          break;

        case 'session_terminated':
          this.logout();
          break;
      }

      const hasToken = this.oauthService.hasValidAccessToken();
      if (!hasToken) {
        this.stopSessionEvents();
      }

      if (this.isAuthenticatedSubject$.value !== hasToken) {
        this.isAuthenticatedSubject$.next(hasToken);
      }
    });

    configService.isInitialized$
      .pipe(
        first(),
        map(() => configService.getConfig()),
        map((x) => authConfigMap(x)),
        tap((authConfigModel) => this.authConfigModel$.next(authConfigModel)),
        map((x) => createAuthConfig(x, showDebugInformation))
      )
      .subscribe((x) => {
        this.oauthService.configure(x);

        const hasToken = this.oauthService.hasValidAccessToken();
        if (navigator?.onLine === false && hasToken) {
          this.isAuthenticatedSubject$.next(true);

          fromEvent(window, 'online')
            .pipe(first())
            .subscribe(() => this.initializeOAuth());
        } else {
          this.initializeOAuth();
        }

        if (hasToken) {
          this.setUserIdFromAccount();
        }
      });
  }

  ngOnDestroy(): void {
    this.oauthEventsSubscription.unsubscribe();
    this.stopSessionEvents();
  }

  get account(): IAccount {
    const claims = this.getClaims();
    return {
      id: claims?.sub,
      displayName: claims?.name,
    };
  }

  get email(): string | undefined {
    return this.getClaims()?.email;
  }

  get idp(): string | undefined {
    return this.getClaims()?.idp;
  }

  get authTime(): string | undefined {
    return this.getClaims().auth_time;
  }

  get issuer(): string | undefined {
    return this.oauthService.issuer;
  }

  private setUserIdFromAccount(): void {
    this.setUserId(this.account.id);
  }

  private initializeOAuth(): void {
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        this.oauthService.restartSessionChecksIfStillLoggedIn();
        this.startSessionEvents();
      })
      .finally(() => this.isLoadedSubject.next(true));
  }

  private startSessionEvents(): void {
    this.stopSessionEvents();

    if (!this.oauthService.hasValidAccessToken() || typeof EventSource === 'undefined') {
      return;
    }

    const issuer = this.oauthService.issuer;
    const clientId = this.oauthService.clientId;
    if (!issuer || !clientId) {
      return;
    }

    const url = `${issuer.replace(/\/$/, '')}/session-events?clientId=${encodeURIComponent(clientId)}`;
    this.sessionEvents = new EventSource(url, { withCredentials: true });
    this.sessionEvents.addEventListener(ServerSentEvents.END_SESSION, () => this.logout(), { once: true });
  }

  private stopSessionEvents(): void {
    this.sessionEvents?.close();
    this.sessionEvents = undefined;
  }

  private getClaims(): OAuthClaims {
    return this.oauthService.getIdentityClaims() as OAuthClaims;
  }

  private get state(): string {
    return localStorage.getItem(this.authState) ?? '';
  }

  private set state(value: string) {
    localStorage.setItem(this.authState, value);
  }

  private clearState(): void {
    localStorage.removeItem(this.authState);
  }

  viewAccount(): void {
    this.authConfigModel$
      .pipe(
        map((config) => config?.myAccountUrl),
        filter((url): url is string => typeof url === 'string' && url.length > 0),
        first()
      )
      .subscribe((url) => {
        window.location.assign(url);
      });
  }

  get tokenEndpoint$(): Observable<string> {
    return this.isAuthenticated$.pipe(map(() => this.oauthService.tokenEndpoint as string));
  }

  get issuerUrl$(): Observable<string> {
    return this.isLoaded$.pipe(map(() => this.oauthService.issuer as string));
  }

  requiresReAuthentication(): Observable<boolean> {
    return this.isAuthenticated$.pipe(
      filter((x) => !!x),
      first(),
      map(() => {
        const authTimeString = this.authTime;
        const reAuthenticationMaxAgeInSeconds = this.authConfigModel$.getValue()?.reAuthenticationMaxAgeInSeconds;

        if (!authTimeString || !reAuthenticationMaxAgeInSeconds) {
          return false;
        }

        const authTime: number = Number.parseInt(authTimeString);
        const now: number = Math.floor(Date.now() / 1000);
        return now - authTime >= reAuthenticationMaxAgeInSeconds;
      })
    );
  }

  reAuthenticate(targetUrl?: string): void {
    this.login(targetUrl, this.idp, true, this.email);
  }

  login(targetUrl?: string, idpHint?: string, prompt?: boolean, loginHint?: string): void {
    targetUrl ??= this.router.url;
    this.state = targetUrl;

    // customQueryParams may already contain values
    const queryParams: Record<string, string> = this.oauthService.customQueryParams as Record<string, string>;

    const currentOrganization = this.dynamicScopesService.get('planzer.id.orgid');
    if (currentOrganization != null) {
      const scopes = this.oauthService.scope?.split(' ').filter((s) => !s.startsWith('.default'));
      scopes?.unshift(`.default?orgId=${currentOrganization}`);
      scopes?.unshift(`${queryParams['resource']}/users:me`);
      this.oauthService.scope = scopes?.join(' ');
    }

    // Optional Parameters
    if (idpHint) {
      queryParams['acr_values'] = `idp:${idpHint}`;
    }

    if (prompt) {
      queryParams['prompt'] = 'login';
    }

    if (loginHint) {
      queryParams['login_hint'] = loginHint;
    }

    this.oauthService.customQueryParams = queryParams;
    this.oauthService.initLoginFlow();
  }

  override logout(): void {
    if (this.logoutInProgress) {
      return;
    }

    this.logoutInProgress = true;
    this.stopSessionEvents();
    this.clearState();
    this.dynamicScopesService.clear();
    this.oauthService.stopAutomaticRefresh();
    super.logout();
    this.oauthService.logOut();
  }

  private handleAuthenticationError(event: OAuthErrorEvent): void {
    const params = event.params as { error?: string } | undefined;
    const response = event.reason as { error?: { error?: string } } | undefined;
    const error = params?.error ?? response?.error?.error;
    const status = error === 'access_denied' || error === 'invalid_scope' ? '403' : '401';

    this.clearState();
    if (status === '403') {
      this.dynamicScopesService.clear();
      this.oauthService.stopAutomaticRefresh();
    }

    this.router.navigate(['errors', status], { relativeTo: null });
  }
}
